import { useEffect, useState, useRef } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  HStack,
  Spinner,
  Tag,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ExternalLinkIcon,
  CopyIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import axios from "axios";
import { config } from "../config";
import {
  formatCoordinates,
  formatList,
  formatPages,
  pageNumbers,
  parseProvenance,
  primaryName,
} from "../utils";
import { ProvenanceMarker } from "./ProvenanceMarker";
import Papa from "papaparse";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const commodityColors = {
  gold: "yellow",
  silver: "gray",
  copper: "orange",
  zinc: "blue",
  lead: "purple",
  nickel: "green",
  lithium: "cyan",
  cobalt: "pink",
  iron: "red",
  uranium: "teal",
};

const getCommodityColor = (commodity) => {
  const lower = commodity.toLowerCase();
  return commodityColors[lower] || "gray";
};

const SectionCopyButton = ({ content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!content) return;

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!content) return null;

  return (
    <Button
      size="xs"
      variant="ghost"
      onClick={handleCopy}
      colorScheme={copied ? "green" : "gray"}
      minW="auto"
      h="auto"
      p={1}
    >
      {copied ? (
        <Text fontSize="xs" color="green.500">
          Copied!
        </Text>
      ) : (
        <CopyIcon boxSize={3} />
      )}
    </Button>
  );
};

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadJSON = (data, filename) =>
  downloadFile(JSON.stringify(data, null, 2), filename, "application/json");

const slugify = (text, maxLength) =>
  String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength);

// e.g. "2024-10-31-westgold-resources-limited-table-3-mineral-resources.csv"
const tableFileName = (reportId, tableIndex, caption) => {
  const parts = [slugify(reportId, 50), `table-${tableIndex + 1}`];
  const captionSlug = caption ? slugify(caption, 40) : "";
  if (captionSlug) parts.push(captionSlug);
  return `${parts.filter(Boolean).join("-")}.csv`;
};

const TableDisplay = ({ table, tableIndex, reportId, onPageClick }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    if (!table?.structured_text) return;

    // Parse CSV using PapaParse
    const parsed = Papa.parse(table.structured_text, {
      header: false,
      skipEmptyLines: true,
    });

    if (parsed.data && parsed.data.length > 0) {
      // First row(s) might be headers - try to detect
      const rows = parsed.data;

      // Find the first row that looks like a header (has non-empty values)
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(3, rows.length); i++) {
        const row = rows[i];
        const nonEmptyCount = row.filter(
          (cell) => cell && cell.trim() !== ""
        ).length;
        if (nonEmptyCount > 2) {
          headerRowIndex = i;
          break;
        }
      }

      // Use the detected header row
      const headerRow = rows[headerRowIndex];
      const dataRows = rows.slice(headerRowIndex + 1);

      // Create columns from header
      const maxCols = Math.max(
        ...rows.map((row) => row.length),
        headerRow ? headerRow.length : 0
      );

      const tableColumns = Array.from({ length: maxCols }, (_, i) => ({
        accessorKey: `col${i}`,
        header:
          headerRow && headerRow[i] ? headerRow[i].trim() : `Column ${i + 1}`,
        cell: (info) => {
          const value = info.getValue();
          return value || "";
        },
      }));

      // Create data rows
      const tableData = dataRows.map((row, rowIdx) => {
        const rowObj = {};
        tableColumns.forEach((col, colIdx) => {
          rowObj[col.accessorKey] = row[colIdx] || "";
        });
        return rowObj;
      });

      setColumns(tableColumns);
      setData(tableData);
    }
  }, [table]);

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!table?.structured_text) {
    return (
      <Text fontSize="sm" color="slate.500" fontStyle="italic">
        No table data available
      </Text>
    );
  }

  const handleDownloadCSV = () => {
    // Papa.unparse handles quoting/escaping of commas, quotes and newlines.
    const csv = Papa.unparse({
      fields: columns.map((column) => column.header),
      data: data.map((row) => columns.map((column) => row[column.accessorKey])),
    });
    downloadFile(
      csv,
      tableFileName(reportId, tableIndex, table.caption),
      "text/csv;charset=utf-8;"
    );
  };

  return (
    <Box>
      <Flex justify="space-between" align="start" gap={3} mb={3}>
        {table.caption ? (
          <Text fontSize="sm" fontWeight="semibold" color="slate.700">
            {table.caption}
          </Text>
        ) : (
          <Box />
        )}
        {data.length > 0 && (
          <Button
            size="xs"
            variant="outline"
            colorScheme="amber"
            leftIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            flexShrink={0}
          >
            Download CSV
          </Button>
        )}
      </Flex>
      <Box
        overflowX="auto"
        border="1px solid"
        borderColor="slate.200"
        borderRadius="md"
      >
        <Box as="table" width="100%" borderCollapse="collapse">
          <Box as="thead" bg="slate.50">
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <Box as="tr" key={headerGroup.id}>
                {headerGroup.headers.map((header, headerIdx) => (
                  <Box
                    as="th"
                    key={header.id}
                    px={4}
                    py={2}
                    textAlign="left"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="slate.700"
                    textTransform="uppercase"
                    borderBottom="1px solid"
                    borderColor="slate.200"
                    borderRight={
                      headerIdx < headerGroup.headers.length - 1
                        ? "1px solid"
                        : "none"
                    }
                    whiteSpace="nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
          <Box as="tbody">
            {tableInstance.getRowModel().rows.map((row) => (
              <Box
                as="tr"
                key={row.id}
                _hover={{ bg: "slate.50" }}
                borderBottom="1px solid"
                borderColor="slate.100"
              >
                {row.getVisibleCells().map((cell, cellIdx) => (
                  <Box
                    as="td"
                    key={cell.id}
                    px={4}
                    py={2}
                    fontSize="sm"
                    color="slate.700"
                    borderRight={
                      cellIdx < row.getVisibleCells().length - 1
                        ? "1px solid"
                        : "none"
                    }
                    borderColor="slate.100"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {formatPages(table.page) && (
        <Text fontSize="xs" color="slate.500" mt={2}>
          <Text
            as="span"
            color="amber.600"
            fontWeight="semibold"
            cursor="pointer"
            _hover={{ textDecoration: "underline", color: "amber.700" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onPageClick) {
                onPageClick(table.page);
              }
            }}
          >
            {formatPages(table.page)}
          </Text>
        </Text>
      )}
    </Box>
  );
};

export const ReportDetail = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const pdfIframeRef = useRef(null);
  const [showIrrelevantSections, setShowIrrelevantSections] = useState(false);
  const [pdfPage, setPdfPage] = useState(null);

  const handlePageClick = (page) => {
    // `page` is a list of page numbers; open the first one.
    const pageNum = pageNumbers(page)[0];
    if (pageNum !== undefined && pdfUrl) {
      setPdfPage(pageNum);

      // Also try to update iframe directly as fallback
      if (pdfIframeRef.current) {
        // Try different page navigation formats
        const baseUrl = pdfUrl.split("#")[0];
        pdfIframeRef.current.src = `${baseUrl}#page=${pageNum}&toolbar=1`;
      }

      // Scroll to PDF viewer
      setTimeout(() => {
        if (pdfIframeRef.current) {
          pdfIframeRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 200);
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${config.API_URL}/43101_reports/${reportId}`,
          { withCredentials: true }
        );
        setReport(response.data.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching report:", err);
        setError("Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  // Use direct API URL instead of blob URL to support range requests
  const pdfUrl = report?.id
    ? `${config.API_URL}/43101_reports/${report.id}/pdf`
    : null;

  if (loading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="slate.200"
            color="amber.500"
            size="xl"
          />
          <Text color="slate.600">Loading report...</Text>
        </VStack>
      </Center>
    );
  }

  if (error || !report) {
    return (
      <Box p={8}>
        <Button
          as={RouterLink}
          to="/reports"
          leftIcon={<ArrowBackIcon />}
          mb={4}
          colorScheme="amber"
        >
          Back to Reports
        </Button>
        <Center minH="50vh">
          <Text fontSize="xl" color="red.500">
            {error || "Report not found"}
          </Text>
        </Center>
      </Box>
    );
  }

  // Filter out irrelevant sections (case-insensitive) based on user preference
  const irrelevantSections = [
    "EMPTY_SECTION",
    "TABLE OF CONTENTS",
    "TABLE OF CONTENTS AND LISTS",
  ];

  const filteredSections =
    report?.sections?.filter((section) => {
      // If user wants to show irrelevant sections, include everything
      if (showIrrelevantSections) {
        return true;
      }

      // Otherwise, filter out irrelevant sections
      const sectionType = (section.section_type || "").toUpperCase().trim();
      const title = (section.title || "").toUpperCase().trim();

      return !irrelevantSections.some(
        (irrelevant) =>
          sectionType === irrelevant.toUpperCase() ||
          title === irrelevant.toUpperCase()
      );
    }) || [];

  const reportUrl = `${config.API_URL}/43101_reports/${report.id}/pdf`;

  // Evidence behind each extracted attribute, keyed by attribute name.
  const provenance = parseProvenance(report.provenance);
  const coordinates = formatCoordinates(report.location_coordinates);

  return (
    <Box
      bg="linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)"
      minH="100vh"
      py={4}
      px={4}
    >
      <Box maxW="100%" mx="auto">
        {/* Back button */}
        <Button
          as={RouterLink}
          to="/reports"
          leftIcon={<ArrowBackIcon />}
          mb={6}
          colorScheme="amber"
          variant="outline"
        >
          Back to Reports
        </Button>

        {/* Header Card */}
        <Box
          bg="white"
          borderRadius="xl"
          shadow="lg"
          overflow="hidden"
          border="1px solid"
          borderColor="slate.200"
          mb={6}
        >
          <Box bg="linear-gradient(135deg, #1e293b 0%, #334155 100%)" p={6}>
            <Flex justify="space-between" align="start" mb={4}>
              <Box flex={1} pr={4}>
                <Text color="white" fontSize="2xl" fontWeight="bold" mb={2}>
                  {primaryName(report.project_name, "Untitled Project")}
                  <ProvenanceMarker
                    label="Project Name"
                    entries={provenance.project_name}
                    onPageClick={handlePageClick}
                    color="whiteAlpha.700"
                    hoverColor="amber.300"
                  />
                </Text>
                <Text color="amber.300" fontSize="md" fontWeight="medium">
                  {formatList(report.company_name, " | ")}
                  <ProvenanceMarker
                    label="Company Name"
                    entries={provenance.company_name}
                    onPageClick={handlePageClick}
                    color="amber.200"
                    hoverColor="white"
                  />
                </Text>
              </Box>
              <HStack spacing={3}>
                <Button
                  as="a"
                  href={reportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="md"
                  colorScheme="amber"
                  rightIcon={<ExternalLinkIcon />}
                >
                  View PDF
                </Button>
                <Button
                  size="md"
                  colorScheme="green"
                  //   variant="outline"
                  rightIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadJSON(report, `report-${report.id}.json`)
                  }
                >
                  Download JSON
                </Button>
              </HStack>
            </Flex>

            {/* Commodities and Report Type */}
            <Wrap spacing={2}>
              {report.commodity &&
                report.commodity.length > 0 &&
                report.commodity.map((c, idx) => (
                  <WrapItem key={idx}>
                    <Tag
                      size="md"
                      colorScheme={getCommodityColor(c)}
                      borderRadius="full"
                      textTransform="capitalize"
                    >
                      {c}
                    </Tag>
                  </WrapItem>
                ))}
              {provenance.commodity && (
                <WrapItem alignItems="center">
                  <ProvenanceMarker
                    label="Commodity"
                    entries={provenance.commodity}
                    onPageClick={handlePageClick}
                    color="whiteAlpha.700"
                    hoverColor="amber.300"
                  />
                </WrapItem>
              )}
              {report.report_type && (
                <WrapItem alignItems="center">
                  <Tag
                    size="md"
                    colorScheme="blue"
                    borderRadius="full"
                    variant="outline"
                  >
                    {report.report_type}
                  </Tag>
                  <ProvenanceMarker
                    label="Report Type"
                    entries={provenance.report_type}
                    onPageClick={handlePageClick}
                    color="whiteAlpha.700"
                    hoverColor="amber.300"
                  />
                </WrapItem>
              )}
            </Wrap>
          </Box>

          {/* Meta Information */}
          <Box p={6}>
            <VStack spacing={4} align="stretch">
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box>
                  <Text
                    fontSize="xs"
                    color="slate.500"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={1}
                  >
                    Report Date
                    <ProvenanceMarker
                      label="Report Date"
                      entries={provenance.report_date}
                      onPageClick={handlePageClick}
                    />
                  </Text>
                  <Text fontSize="sm" color="slate.800" fontWeight="medium">
                    {report.report_date || "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="slate.500"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={1}
                  >
                    Location
                    <ProvenanceMarker
                      label="Location"
                      entries={provenance.project_location}
                      onPageClick={handlePageClick}
                    />
                  </Text>
                  <Text fontSize="sm" color="slate.800" fontWeight="medium">
                    {report.project_location || "N/A"}
                  </Text>
                </Box>
                {(coordinates || provenance.location_coordinates) && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color="slate.500"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      mb={1}
                    >
                      Coordinates
                      <ProvenanceMarker
                        label="Coordinates"
                        entries={provenance.location_coordinates}
                        onPageClick={handlePageClick}
                      />
                    </Text>
                    <Text fontSize="sm" color="slate.800" fontWeight="medium">
                      {coordinates || "N/A"}
                    </Text>
                  </Box>
                )}
                {report.deposit_style && (
                  <Box gridColumn="1 / -1">
                    <Text
                      fontSize="xs"
                      color="slate.500"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      mb={1}
                    >
                      Deposit Style
                      <ProvenanceMarker
                        label="Deposit Style"
                        entries={provenance.deposit_style}
                        onPageClick={handlePageClick}
                      />
                    </Text>
                    <Text fontSize="sm" color="slate.800" fontWeight="medium">
                      {report.deposit_style}
                    </Text>
                  </Box>
                )}
                {report.report_authors?.length > 0 && (
                  <Box gridColumn="1 / -1">
                    <Text
                      fontSize="xs"
                      color="slate.500"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      mb={1}
                    >
                      Authors
                      <ProvenanceMarker
                        label="Authors"
                        entries={provenance.report_authors}
                        onPageClick={handlePageClick}
                      />
                    </Text>
                    <Text fontSize="sm" color="slate.800" fontWeight="medium">
                      {formatList(report.report_authors)}
                    </Text>
                  </Box>
                )}
              </Grid>

              {/* Mineral Resources */}
              {(report.mineral_resources_indicated ||
                report.mineral_resources_inferred) && (
                <Accordion allowMultiple defaultIndex={[0, 1]}>
                  {report.mineral_resources_indicated && (
                    <AccordionItem border="none">
                      <Flex align="center">
                        <AccordionButton
                          px={0}
                          w="auto"
                          flex="0 0 auto"
                          _hover={{ bg: "transparent" }}
                        >
                          <Text
                            fontSize="xs"
                            color="slate.500"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Indicated Resources
                          </Text>
                          <AccordionIcon ml={2} color="slate.500" />
                        </AccordionButton>
                        <ProvenanceMarker
                          label="Indicated Resources"
                          entries={provenance.mineral_resources_indicated}
                          onPageClick={handlePageClick}
                        />
                      </Flex>
                      <AccordionPanel px={0} pb={2}>
                        <Text fontSize="sm" color="slate.600" lineHeight="tall">
                          {report.mineral_resources_indicated}
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                  {report.mineral_resources_inferred && (
                    <AccordionItem border="none">
                      <Flex align="center">
                        <AccordionButton
                          px={0}
                          w="auto"
                          flex="0 0 auto"
                          _hover={{ bg: "transparent" }}
                        >
                          <Text
                            fontSize="xs"
                            color="slate.500"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Inferred Resources
                          </Text>
                          <AccordionIcon ml={2} color="slate.500" />
                        </AccordionButton>
                        <ProvenanceMarker
                          label="Inferred Resources"
                          entries={provenance.mineral_resources_inferred}
                          onPageClick={handlePageClick}
                        />
                      </Flex>
                      <AccordionPanel px={0} pb={2}>
                        <Text fontSize="sm" color="slate.600" lineHeight="tall">
                          {report.mineral_resources_inferred}
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                </Accordion>
              )}

              {/* Notes */}
              {report.notes && (
                <Box>
                  <Text
                    fontSize="xs"
                    color="slate.500"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={1}
                  >
                    Notes
                  </Text>
                  <Text fontSize="sm" color="slate.600" lineHeight="tall">
                    {report.notes}
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        </Box>

        {/* PDF Viewer */}
        <Box
          maxW="70%"
          mx="auto"
          bg="white"
          borderRadius="xl"
          shadow="lg"
          border="1px solid"
          borderColor="slate.200"
          overflow="hidden"
          h="calc(100vh - 300px)"
          minH="600px"
          position="relative"
          mb={6}
        >
          {pdfError ? (
            <Center h="100%">
              <VStack spacing={4}>
                <Text color="red.500" fontSize="lg" fontWeight="semibold">
                  Failed to load PDF
                </Text>
                <Text color="slate.600" fontSize="sm">
                  {pdfError}
                </Text>
                <Button
                  colorScheme="amber"
                  onClick={() => {
                    setPdfError(null);
                    // Force iframe reload
                    if (pdfIframeRef.current) {
                      pdfIframeRef.current.src = pdfIframeRef.current.src;
                    }
                  }}
                >
                  Retry
                </Button>
              </VStack>
            </Center>
          ) : pdfUrl ? (
            <iframe
              ref={pdfIframeRef}
              src={
                pdfPage
                  ? `${pdfUrl}#page=${pdfPage}&toolbar=1`
                  : `${pdfUrl}#toolbar=1`
              }
              width="100%"
              height="100%"
              style={{
                border: "none",
              }}
              title={`PDF Report: ${primaryName(report.project_name, report.id)}`}
              key={pdfPage || "default"}
              onError={(e) => {
                console.error("Iframe error loading PDF:", e);
                setPdfError("Failed to display PDF in iframe. Please check your network connection and try again.");
              }}
              onLoad={() => {
                console.log("PDF iframe loaded successfully");
                setPdfError(null);
              }}
            />
          ) : (
            <Center h="100%">
              <Text color="red.500">No PDF URL available</Text>
            </Center>
          )}
        </Box>

        {/* Tables and Sections in Tabs */}
        {((report?.tables && report.tables.length > 0) ||
          (report?.sections && report.sections.length > 0)) && (
          <Box
            bg="white"
            borderRadius="xl"
            shadow="lg"
            border="1px solid"
            borderColor="slate.200"
            p={6}
            mt={6}
          >
            <Tabs colorScheme="amber">
              <TabList>
                {report?.tables && report.tables.length > 0 && (
                  <Tab>Tables ({report.tables.length})</Tab>
                )}
                {report?.sections && report.sections.length > 0 && (
                  <Tab>
                    Sections ({filteredSections.length}
                    {!showIrrelevantSections &&
                      report.sections.length > filteredSections.length &&
                      ` of ${report.sections.length}`}
                    )
                  </Tab>
                )}
              </TabList>

              <TabPanels>
                {report?.tables && report.tables.length > 0 && (
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {report.tables.map((table, idx) => (
                        <Box
                          key={idx}
                          border="1px solid"
                          borderColor="slate.200"
                          borderRadius="lg"
                          p={4}
                        >
                          <TableDisplay
                            table={table}
                            tableIndex={idx}
                            reportId={report.id}
                            onPageClick={handlePageClick}
                          />
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>
                )}

                {report?.sections && report.sections.length > 0 && (
                  <TabPanel>
                    <Flex justify="space-between" align="center" mb={6}>
                      <Box />
                      <Checkbox
                        isChecked={showIrrelevantSections}
                        onChange={(e) =>
                          setShowIrrelevantSections(e.target.checked)
                        }
                        colorScheme="amber"
                        size="sm"
                      >
                        <Text fontSize="sm" color="slate.600">
                          Show irrelevant sections
                        </Text>
                      </Checkbox>
                    </Flex>
                    {filteredSections.length > 0 ? (
                      <Accordion allowMultiple defaultIndex={[]}>
                        <VStack spacing={3} align="stretch">
                          {filteredSections.map((section, idx) => (
                            <AccordionItem
                              key={idx}
                              border="1px solid"
                              borderColor="slate.200"
                              borderRadius="lg"
                              overflow="hidden"
                            >
                              {({ isExpanded }) => (
                                <>
                                  <AccordionButton
                                    bg={isExpanded ? "slate.50" : "white"}
                                    _hover={{ bg: "slate.50" }}
                                    py={3}
                                    flexDirection="column"
                                    alignItems="flex-start"
                                  >
                                    <Flex
                                      width="100%"
                                      justify="space-between"
                                      align="start"
                                    >
                                      <Box flex="1" textAlign="left">
                                        <HStack spacing={2} mb={1}>
                                          {formatPages(section.page) && (
                                            <Badge
                                              colorScheme="amber"
                                              fontSize="xs"
                                              fontWeight="semibold"
                                            >
                                              {formatPages(section.page)}
                                            </Badge>
                                          )}
                                          {section.section_type &&
                                            section.section_type !==
                                              "unclassified" && (
                                              <Badge
                                                colorScheme="blue"
                                                fontSize="xs"
                                              >
                                                {section.section_type}
                                              </Badge>
                                            )}
                                        </HStack>
                                        {section.title && (
                                          <Text
                                            fontWeight="semibold"
                                            color="slate.800"
                                            fontSize="sm"
                                          >
                                            {section.title}
                                          </Text>
                                        )}
                                      </Box>
                                      <AccordionIcon ml={2} />
                                    </Flex>
                                    {isExpanded && (
                                      <Box mt={2} alignSelf="flex-start">
                                        <SectionCopyButton
                                          content={section.content}
                                        />
                                      </Box>
                                    )}
                                  </AccordionButton>
                                  <AccordionPanel pb={4} px={4}>
                                    {section.content ? (
                                      <Text
                                        fontSize="sm"
                                        color="slate.700"
                                        whiteSpace="pre-wrap"
                                        lineHeight="tall"
                                      >
                                        {section.content}
                                      </Text>
                                    ) : (
                                      <Text
                                        fontSize="sm"
                                        color="slate.500"
                                        fontStyle="italic"
                                      >
                                        No content available
                                      </Text>
                                    )}
                                  </AccordionPanel>
                                </>
                              )}
                            </AccordionItem>
                          ))}
                        </VStack>
                      </Accordion>
                    ) : (
                      <Text
                        fontSize="sm"
                        color="slate.500"
                        fontStyle="italic"
                        textAlign="center"
                        py={4}
                      >
                        No relevant sections to display. Toggle "Show irrelevant
                        sections" to see all sections.
                      </Text>
                    )}
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
        )}
      </Box>
    </Box>
  );
};
